import { X } from 'lucide-react';
import { useState } from 'react';
import styles from '../SegmentVisualBuilder.module.css';
import type { BuilderConditionValue } from '../segment-builder.types';
import { SuggestedTextInput } from './SuggestedTextInput';

interface TokenizedValueInputProps {
  value: BuilderConditionValue[];
  onChange: (value: BuilderConditionValue[]) => void;
  disabled?: boolean;
  /** Number fields compile to a ::numeric comparison, so a non-numeric token errors at query time. */
  numeric?: boolean;
  schemaId?: string;
  field?: string;
}

function parseValues(rawValue: string): string[] {
  return rawValue
    .split(',')
    .map(token => token.trim())
    .filter(Boolean);
}

const NUMERIC_TOKEN_HINT = 'Enter a number';

function isNumericToken(token: string): boolean {
  return Number.isFinite(Number(token));
}

/** Token text for display, comparison, and accessible labels; non-string values keep their stored type. */
function tokenLabel(value: BuilderConditionValue): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function TokenizedValueInput({
  value,
  onChange,
  disabled,
  numeric = false,
  schemaId,
  field,
}: TokenizedValueInputProps) {
  const [inputValue, setInputValue] = useState('');

  const addValues = (rawValue: string) => {
    const existingLabels = new Set(value.map(tokenLabel));
    const newValues: BuilderConditionValue[] = [];
    for (const parsed of parseValues(rawValue)) {
      if (existingLabels.has(parsed)) continue;
      if (numeric && !isNumericToken(parsed)) continue;
      existingLabels.add(parsed);
      newValues.push(numeric ? Number(parsed) : parsed);
    }
    if (newValues.length > 0) onChange([...value, ...newValues]);
  };

  const commitInputValue = (nextValue: string) => {
    addValues(nextValue);
    setInputValue('');
  };

  const handleInputValueChange = (nextInputValue: string) => {
    if (nextInputValue.includes(',')) {
      const inputParts = nextInputValue.split(',');
      const pendingValue = inputParts.pop() ?? '';
      addValues(inputParts.join(','));
      setInputValue(pendingValue);
    } else {
      setInputValue(nextInputValue);
    }
  };

  const removeValueAt = (indexToRemove: number) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  const pendingToken = inputValue.trim();
  const pendingTokenIsInvalid = numeric && pendingToken !== '' && !isNumericToken(pendingToken);

  return (
    <div className={styles.conditionRowValue}>
      <div className={styles.tokenInput}>
        {value.map((item, index) => {
          const label = tokenLabel(item);
          return (
            <span key={`${label}-${index}`} className={styles.token}>
              {label}
              <button
                type="button"
                className={styles.tokenRemoveButton}
                onClick={() => removeValueAt(index)}
                disabled={disabled}
                aria-label={`Remove ${label}`}>
                <X size={14} aria-hidden />
              </button>
            </span>
          );
        })}
        <SuggestedTextInput
          value={inputValue}
          onChange={handleInputValueChange}
          onCommit={commitInputValue}
          disabled={disabled}
          placeholder={value.length === 0 ? 'Type a value, then press Enter' : 'Add another'}
          ariaLabel="Values"
          ariaInvalid={pendingTokenIsInvalid}
          inputClassName={styles.tokenInputField}
          embedded
          schemaId={numeric ? undefined : schemaId}
          field={numeric ? undefined : field}
        />
      </div>
      {pendingTokenIsInvalid && (
        <span className={styles.validationMessage} role="alert">
          {NUMERIC_TOKEN_HINT}
        </span>
      )}
    </div>
  );
}
